import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  AssociatedCampaignAsset,
  useCreateAssociatedCampaignAssetMutation,
  useGetAssociatedCampaignQuery,
  useGetBriefingByIdQuery,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import KeyValueEditor from "../common/KeyValueEditor";
import AssetDestinationsEditor, {
  DestinationDraft,
  createDestinationDraft,
} from "./AssetDestinationsEditor";

interface AssociatedCampaignAssetFormProps {
  briefingId: string;
  associatedId: string;
  initialAsset: AssociatedCampaignAsset | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// ...

interface FormState {
  assetsLinks: Record<string, string>;
  copiesLinks: Record<string, string>;
  additionalLinks: Record<string, string>;
}

const defaultState: FormState = {
  assetsLinks: {},
  copiesLinks: {},
  additionalLinks: {},
};

const toUtmToken = (value: string) =>
  encodeURIComponent(value.trim().toLowerCase().replace(/\s+/g, "_"));

/** Builds the destination rows from an asset, supporting the legacy single URL. */
const toDestinationDrafts = (
  asset: AssociatedCampaignAsset | null
): DestinationDraft[] => {
  if (!asset) return [];

  if (Array.isArray(asset.destinations) && asset.destinations.length > 0) {
    return asset.destinations.map((destination) =>
      createDestinationDraft({
        id: destination.id ?? undefined,
        label: destination.label ?? "",
        url: destination.url ?? "",
        utm: destination.utm ?? "",
        isAutoUtm: destination.isAutoUtm ?? true,
      })
    );
  }

  if (asset.destinationUrl || asset.utm) {
    return [
      createDestinationDraft({
        url: asset.destinationUrl ?? "",
        utm: asset.utm ?? "",
        isAutoUtm: asset.isAutoUtm ?? !asset.utm,
      }),
    ];
  }

  return [];
};

const AssociatedCampaignAssetForm: React.FC<
  AssociatedCampaignAssetFormProps
> = ({ briefingId, associatedId, initialAsset, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [createAsset, { isLoading }] =
    useCreateAssociatedCampaignAssetMutation();

  const [formState, setFormState] = useState<FormState>(defaultState);
  const [destinations, setDestinations] = useState<DestinationDraft[]>(() =>
    toDestinationDrafts(initialAsset)
  );

  // Fetch contextual data for UTM automation
  const { data: briefingData } = useGetBriefingByIdQuery({ id: briefingId });
  const { data: associatedData } = useGetAssociatedCampaignQuery({
    briefingId,
    associatedId,
  });

  const briefing = briefingData?.data;
  const associatedCampaign = associatedData?.data;

  const utmContext = useMemo(
    () => ({
      source: toUtmToken(
        associatedCampaign?.briefingCampaignType ?? "UNDEFINED"
      ),
      medium: toUtmToken(associatedCampaign?.platform ?? "platform"),
      campaign: toUtmToken(
        associatedCampaign?.goal?.campaign ||
          associatedCampaign?.name ||
          briefing?.campaignName ||
          briefing?.name ||
          "campaign"
      ),
    }),
    [
      associatedCampaign?.briefingCampaignType,
      associatedCampaign?.platform,
      associatedCampaign?.name,
      associatedCampaign?.goal?.campaign,
      briefing?.campaignName,
      briefing?.name,
    ]
  );

  // Mirrors the backend UTM composition so the preview matches what gets stored.
  const buildAutoUtm = useCallback(
    (destination: DestinationDraft) => {
      const url = destination.url.trim();
      if (!url) return "";

      const separator = url.includes("?") ? "&" : "?";
      const query = [
        `utm_source=${utmContext.source}`,
        `utm_medium=${utmContext.medium}`,
        `utm_campaign=${utmContext.campaign}`,
      ];

      if (destination.label.trim()) {
        query.push(`utm_content=${toUtmToken(destination.label)}`);
      }

      return `${url}${separator}${query.join("&")}`;
    },
    [utmContext]
  );

  // Helper to ensure we have a Record (if initial is string or mixed)
  const toRecord = (val: unknown): Record<string, string> => {
    if (!val) return {};
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        if (typeof parsed === "object" && parsed !== null)
          return parsed as Record<string, string>;
      } catch {
        return val.trim() ? { link: val } : {};
      }
    }
    return val as Record<string, string>;
  };

  useEffect(() => {
    if (initialAsset) {
      setFormState({
        assetsLinks: toRecord(initialAsset.assetsLinks),
        copiesLinks: toRecord(initialAsset.copiesLinks),
        additionalLinks: toRecord(initialAsset.additionalLinks),
      });
      setDestinations(toDestinationDrafts(initialAsset));
    }
  }, [initialAsset]);

  const handleFieldChange = (
    field: keyof FormState,
    value: Record<string, string>
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const payloadDestinations = destinations
      .map((destination) => ({
        id: destination.id,
        label: destination.label.trim() || null,
        url: destination.url.trim() || null,
        utm:
          (destination.isAutoUtm
            ? buildAutoUtm(destination)
            : destination.utm.trim()) || null,
        isAutoUtm: destination.isAutoUtm,
      }))
      .filter((destination) => destination.label || destination.url);

    try {
      await createAsset({
        briefingId,
        associatedId,
        assetsLinks: formState.assetsLinks,
        copiesLinks: formState.copiesLinks,
        additionalLinks: formState.additionalLinks,
        destinations: payloadDestinations,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t(
            "briefingsEdit.assetUpdateSuccess",
            "Assets updated successfully"
          ),
          severity: "success",
        })
      );
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update assets", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsEdit.assetUpdateError",
            "Failed to update assets"
          ),
          severity: "error",
        })
      );
    }
  };

  return (
    <Paper
      elevation={0}
      component="form"
      onSubmit={handleSubmit}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 4 },
      }}
    >
      <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
        {t("briefingsView.assetSectionTitle", "Assets")}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <KeyValueEditor
            label={t("briefingsView.asset.assetsLinks", "Assets links")}
            initialValue={formState.assetsLinks}
            onChange={(val) => handleFieldChange("assetsLinks", val)}
            exampleKey="video"
            exampleValue="https://examples.com/asset"
            helperText={t(
              "briefingsEdit.helper.assetsLinks",
              "Add links to creative assets like videos or images"
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueEditor
            label={t("briefingsView.asset.copiesLinks", "Copies links")}
            initialValue={formState.copiesLinks}
            onChange={(val) => handleFieldChange("copiesLinks", val)}
            exampleKey="doc"
            exampleValue="https://docs.google.com/..."
            helperText={t(
              "briefingsEdit.helper.copiesLinks",
              "Links to copy documents"
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueEditor
            label={t("briefingsView.asset.additionalLinks", "Additional links")}
            initialValue={formState.additionalLinks}
            onChange={(val) => handleFieldChange("additionalLinks", val)}
            exampleKey="other"
            exampleValue="https://..."
          />
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ mb: 3 }} />
          <AssetDestinationsEditor
            destinations={destinations}
            onChange={setDestinations}
            buildAutoUtm={buildAutoUtm}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isLoading}>
            {t("common.cancel", "Cancel")}
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("briefingsEdit.save", "Save Assets")
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default AssociatedCampaignAssetForm;
